import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationsService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { QUERY_KEYS } from '@restworld/utils-common';

export function usePaymentCompanyData() {
  const { user } = useAuth();
  const organizationsService = useOrganizationsService();
  const queryClient = useQueryClient();
  const organizationId = user?.organization_id;

  // Fetch company list
  const companiesQuery = useQuery({
    queryKey: [QUERY_KEYS.ORGANISATIONS.COMPANY_LIST, organizationId],
    queryFn: () =>
      organizationsService.listCompanies({
        limit: 1,
        offset: 0,
        context: 'employer-payment-method-confirmation-listing-companies',
        organizationId,
        filterDefault: true,
      }),
    select: (data) => data.data.data, // Get all companies
    enabled: !!organizationId,
  });

  // Fetch payment methods
  const paymentMethodsQuery = useQuery({
    queryKey: [QUERY_KEYS.ORGANISATIONS.STRIPE_PAYMENT_METHODS, organizationId],
    queryFn: () => organizationsService.fetchStripePaymentMethods({ organizationId }),
    select: (data) => data.data, // Get all payment methods data
    enabled: !!organizationId,
  });

  // Detach payment method mutation
  const detachPaymentMethodMutation = useMutation({
    mutationFn: ({ paymentMethodId }: { paymentMethodId: string }) =>
      organizationsService.detachPaymentMethod({ paymentMethodId }),
    onSuccess: () => {
      // Refetch payment methods to update the list
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANISATIONS.STRIPE_PAYMENT_METHODS, organizationId],
      });
    },
  });

  // Update default payment method mutation
  const updateDefaultPaymentMethodMutation = useMutation({
    mutationFn: ({ stripeCustomerId, paymentMethodId }: { stripeCustomerId: string; paymentMethodId: string }) =>
      organizationsService.updateDefaultPaymentMethod({ stripeCustomerId, paymentMethodId }),
    onSuccess: () => {
      // Refetch payment methods to update the list
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANISATIONS.STRIPE_PAYMENT_METHODS, organizationId],
      });
    },
  });

  // Attach payment method mutation
  const attachPaymentMethodMutation = useMutation({
    mutationFn: ({
      stripeCustomerId,
      paymentMethodId,
      isDefault,
    }: {
      stripeCustomerId: string;
      paymentMethodId: string;
      isDefault?: boolean;
    }) => organizationsService.attachPaymentMethod({ stripeCustomerId, paymentMethodId, isDefault }),
    onSuccess: () => {
      // Refetch payment methods to update the list
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANISATIONS.STRIPE_PAYMENT_METHODS, organizationId],
      });
    },
  });

  return {
    companies: companiesQuery.data || [],
    paymentMethodsData: paymentMethodsQuery.data || [],
    isLoading: companiesQuery.isLoading || paymentMethodsQuery.isLoading,
    isFetching: companiesQuery.isFetching || paymentMethodsQuery.isFetching,
    isError: companiesQuery.isError || paymentMethodsQuery.isError,
    detachPaymentMethod: detachPaymentMethodMutation.mutateAsync,
    isDetaching: detachPaymentMethodMutation.isPending,
    updateDefaultPaymentMethod: updateDefaultPaymentMethodMutation.mutateAsync,
    isUpdatingDefault: updateDefaultPaymentMethodMutation.isPending,
    attachPaymentMethod: attachPaymentMethodMutation.mutateAsync,
    isAttaching: attachPaymentMethodMutation.isPending,
  };
}
